import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import { FaPalette, FaSave, FaTrash, FaEye, FaToggleOn, FaToggleOff, FaCheck, FaTimes, FaPaintBrush, FaGem, FaTshirt, FaBasketballBall, FaTh, FaColumns, FaLayerGroup, FaNewspaper, FaGripHorizontal } from 'react-icons/fa';

const DesignManager = () => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [activeTab, setActiveTab] = useState('designs'); // 'designs' or 'layouts'
  const [layouts, setLayouts] = useState([]);
  const [newLayoutData, setNewLayoutData] = useState({
    name: '',
    description: '',
    structure: 'grid-cards',
    features: [''],
    cssVariables: new Map()
  });

  useEffect(() => {
    fetchThemes();
    fetchLayouts();
  }, []);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/settings/themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setThemes(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch themes');
      console.error('Theme fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLayouts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/settings/layouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setLayouts(response.data.data);
        console.log('Layouts fetched:', response.data.data);
      } else {
        // If no layouts from backend, show default layouts
        const defaultLayouts = [
          {
            _id: 'default-modern',
            name: 'Modern Grid',
            description: 'Clean grid layout with card-based design',
            structure: 'grid-cards',
            features: ['minimal-header', 'card-products', 'sidebar-hidden']
          },
          {
            _id: 'default-classic',
            name: 'Classic Sidebar',
            description: 'Traditional layout with sidebar navigation',
            structure: 'sidebar-content',
            features: ['elegant-header', 'sidebar-nav', 'detailed-cards']
          },
          {
            _id: 'default-showcase',
            name: 'Premium Showcase',
            description: 'Hero section with featured products',
            structure: 'hero-grid',
            features: ['large-hero', 'featured-products', 'elegant-cards']
          },
          {
            _id: 'default-magazine',
            name: 'Magazine Style',
            description: 'Editorial layout with large images',
            structure: 'magazine-layout',
            features: ['hero-banner', 'editorial-cards', 'trending-sidebar']
          },
          {
            _id: 'default-minimal',
            name: 'Minimal Compact',
            description: 'Ultra-minimalist design with clean spacing',
            structure: 'minimal-grid',
            features: ['simple-header', 'compact-cards', 'hidden-sidebar']
          }
        ];
        setLayouts(defaultLayouts);
        console.log('Using default layouts:', defaultLayouts);
      }
    } catch (error) {
      toast.error('Failed to fetch layouts');
      console.error('Layout fetch error:', error);
      // Set default layouts on error
      const defaultLayouts = [
        {
          _id: 'default-modern',
          name: 'Modern Grid',
          description: 'Clean grid layout with card-based design',
          structure: 'grid-cards',
          features: ['minimal-header', 'card-products', 'sidebar-hidden']
        }
      ];
      setLayouts(defaultLayouts);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDesign = async (themeCode, designKey, enabled) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/settings/themes/${themeCode}`, {
        enabled,
        selectedDesign: designKey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setThemes(prev => prev.map(theme => 
        theme.code === themeCode 
          ? { 
              ...theme, 
              enabled,
              selectedDesign: enabled ? designKey : null
            }
          : theme
      ));
      
      toast.success(`Design ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      toast.error('Failed to update design');
      console.error('Design toggle error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLayout = async (themeCode, layoutKey, enabled) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/settings/themes/${themeCode}`, {
        enabled,
        selectedLayout: layoutKey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setThemes(prev => prev.map(theme => 
        theme.code === themeCode 
          ? { 
              ...theme, 
              enabled,
              selectedLayout: enabled ? layoutKey : null
            }
          : theme
      ));
      
      toast.success(`Layout ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      toast.error('Failed to update layout');
      console.error('Layout toggle error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateLayout = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const { name, description, structure, features, cssVariables } = newLayoutData;
      
      await axios.post(`${API_URL}/api/settings/layouts`, {
        name,
        description,
        structure,
        features,
        cssVariables
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Layout created successfully');
      setNewLayoutData({
        name: '',
        description: '',
        structure: 'grid-cards',
        features: [''],
        cssVariables: new Map()
      });
    } catch (error) {
      toast.error('Failed to create layout');
      console.error('Layout creation error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLayout = async (layoutId, layoutData) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/settings/layouts/${layoutId}`, layoutData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Layout updated successfully');
    } catch (error) {
      toast.error('Failed to update layout');
      console.error('Layout update error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLayout = async (layoutId) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API_URL}/api/settings/layouts/${layoutId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Layout deleted successfully');
    } catch (error) {
      toast.error('Failed to delete layout');
      console.error('Layout deletion error:', error);
    } finally {
      setSaving(false);
    }
  };

  const getDesignIcon = (designKey) => {
    const icons = {
      classic: FaGem,
      modern: FaPaintBrush,
      boutique: FaTshirt,
      performance: FaBasketballBall
    };
    return icons[designKey] || FaPalette;
  };

  const getLayoutIcon = (layoutKey) => {
    const icons = {
      modern: FaTh,
      classic: FaColumns,
      showcase: FaLayerGroup,
      magazine: FaNewspaper,
      minimal: FaGripHorizontal
    };
    return icons[layoutKey] || FaPalette;
  };

  const renderDesignCard = (theme, designKey) => {
    const design = theme.config?.designs?.[designKey];
    if (!design) return null;

    const Icon = getDesignIcon(designKey);
    const isSelected = theme.selectedDesign === designKey;

    return (
      <div key={designKey} className={`design-card ${isSelected ? 'selected' : ''}`}>
        <div className="design-preview">
          <div className="design-colors">
            <div 
              className="color-swatch primary"
              style={{ backgroundColor: design.colors.primary }}
            />
            <div 
              className="color-swatch secondary"
              style={{ backgroundColor: design.colors.secondary }}
            />
            <div 
              className="color-swatch accent"
              style={{ backgroundColor: design.colors.accent }}
            />
          </div>
          
          <div className="design-info">
            <div className="design-header">
              <Icon className="design-icon" />
              <h5>{design.name}</h5>
              {isSelected && <span className="selected-badge">Active</span>}
            </div>
            <p>{design.description}</p>
            
            <div className="design-features">
              {design.features.map(feature => (
                <span key={feature} className="feature-tag">{feature}</span>
              ))}
            </div>
          </div>
        </div>
        
        <div className="design-actions">
          <button
            className={`btn btn-sm ${isSelected ? 'btn-warning' : 'btn-success'}`}
            onClick={() => handleToggleDesign(theme.code, designKey, !isSelected)}
            disabled={saving}
          >
            {isSelected ? <FaToggleOff /> : <FaToggleOn />}
            {isSelected ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
    );
  };

  const renderLayoutCard = (theme, layoutKey) => {
    const layout = theme.config?.layoutOptions?.[layoutKey];
    if (!layout) return null;

    const Icon = getLayoutIcon(layoutKey);
    const isSelected = theme.selectedLayout === layoutKey;

    return (
      <div key={layoutKey} className={`layout-card ${isSelected ? 'selected' : ''}`}>
        <div className="layout-preview">
          <div className="layout-visual">
            <Icon className="layout-icon-large" />
            <div className="layout-structure">
              <div className={`structure-preview structure-${layout.structure}`}>
                {layout.structure === 'grid-cards' && (
                  <div className="grid-preview">
                    <div className="preview-card"></div>
                    <div className="preview-card"></div>
                    <div className="preview-card"></div>
                  </div>
                )}
                {layout.structure === 'sidebar-content' && (
                  <div className="sidebar-preview">
                    <div className="preview-sidebar"></div>
                    <div className="preview-content"></div>
                  </div>
                )}
                {layout.structure === 'hero-grid' && (
                  <div className="hero-preview">
                    <div className="preview-hero"></div>
                    <div className="preview-grid">
                      <div className="preview-card"></div>
                      <div className="preview-card"></div>
                    </div>
                  </div>
                )}
                {layout.structure === 'magazine-layout' && (
                  <div className="magazine-preview">
                    <div className="preview-banner"></div>
                    <div className="preview-magazine">
                      <div className="preview-article"></div>
                      <div className="preview-article"></div>
                    </div>
                  </div>
                )}
                {layout.structure === 'minimal-grid' && (
                  <div className="minimal-preview">
                    <div className="preview-card"></div>
                    <div className="preview-card"></div>
                    <div className="preview-card"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="layout-info">
            <div className="layout-header">
              <Icon className="layout-icon" />
              <h5>{layout.name}</h5>
              {isSelected && <span className="selected-badge">Active</span>}
            </div>
            <p>{layout.description}</p>
            
            <div className="layout-features">
              {layout.features.map(feature => (
                <span key={feature} className="feature-tag">{feature}</span>
              ))}
            </div>
          </div>
        </div>
        
        <div className="layout-actions">
          <button
            className={`btn btn-sm ${isSelected ? 'btn-warning' : 'btn-success'}`}
            onClick={() => handleToggleLayout(theme.code, layoutKey, !isSelected)}
            disabled={saving}
          >
            {isSelected ? <FaToggleOff /> : <FaToggleOn />}
            {isSelected ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
    );
  };

  const getThemesByIndustry = () => {
    const themesByIndustry = {
      all: themes,
      jewellery: themes.filter(t => t.industry === 'jewellery'),
      fashion: themes.filter(t => t.industry === 'fashion'),
      sports: themes.filter(t => t.industry === 'sports'),
      electronics: themes.filter(t => t.industry === 'electronics'),
      food: themes.filter(t => t.industry === 'food'),
      beauty: themes.filter(t => t.industry === 'beauty'),
      healthcare: themes.filter(t => t.industry === 'healthcare'),
      home_garden: themes.filter(t => t.industry === 'home_garden'),
      education: themes.filter(t => t.industry === 'education')
    };
    return themesByIndustry;
  };

  return (
    <div className="design-manager">
      <div className="design-manager-header">
        <h2>
          <FaPalette className="mr-2" />
          Design & Layout Management
        </h2>
        <p>Control visual designs and layout structures for each theme</p>
      </div>

      <div className="design-manager-content">
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-btn ${activeTab === 'designs' ? 'active' : ''}`}
            onClick={() => setActiveTab('designs')}
          >
            <FaPaintBrush />
            Design Variations
          </button>
          <button
            className={`tab-btn ${activeTab === 'layouts' ? 'active' : ''}`}
            onClick={() => setActiveTab('layouts')}
          >
            <FaTh />
            Layout Structures
          </button>
        </div>

        {/* Industry Filter */}
        <div className="industry-filter">
          <h3>Filter by Industry</h3>
          <div className="industry-buttons">
            <button
              className={`industry-btn ${selectedIndustry === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('all')}
            >
              All Themes
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'jewellery' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('jewellery')}
            >
              💎 Jewellery
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'fashion' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('fashion')}
            >
              👗 Fashion
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'sports' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('sports')}
            >
              ⚽ Sports
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'electronics' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('electronics')}
            >
              📱 Electronics
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'food' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('food')}
            >
              🍕 Food & Restaurant
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'beauty' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('beauty')}
            >
              💄 Beauty & Salon
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'healthcare' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('healthcare')}
            >
              🏥 Healthcare
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'home_garden' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('home_garden')}
            >
              🏡 Home & Garden
            </button>
            <button
              className={`industry-btn ${selectedIndustry === 'education' ? 'active' : ''}`}
              onClick={() => setSelectedIndustry('education')}
            >
              📚 Education
            </button>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'designs' && (
          <div className="design-section">
            <h3>
              {selectedIndustry === 'all' ? 'All Theme Designs' : `${selectedIndustry.charAt(0).toUpperCase() + selectedIndustry.slice(1).replace('_', ' ')} Designs`}
            </h3>
            <p>
              {selectedIndustry === 'all' 
                ? 'Visual design variations for all themes'
                : `Design variations for ${selectedIndustry.replace('_', ' ')} themes`
              }
            </p>
            
            {loading ? (
              <div className="loading">Loading designs...</div>
            ) : (
              <div className="design-grid">
                {getThemesByIndustry()[selectedIndustry].map((theme) => (
                  <div key={theme.code} className={`theme-designs ${theme.enabled ? 'enabled' : 'disabled'}`}>
                    <div className="theme-header">
                      <h4>{theme.name}</h4>
                      <p>{theme.description}</p>
                      <span className={`theme-status ${theme.enabled ? 'enabled' : 'disabled'}`}>
                        {theme.enabled ? <FaToggleOn /> : <FaToggleOff />}
                        {theme.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    
                    <div className="designs-container">
                      {Object.entries(theme.config?.designs || {}).map(([designKey]) => 
                        renderDesignCard(theme, designKey)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'layouts' && (
          <div className="layout-section">
            <h3>
              {selectedIndustry === 'all' ? 'All Theme Layouts' : `${selectedIndustry.charAt(0).toUpperCase() + selectedIndustry.slice(1).replace('_', ' ')} Layouts`}
            </h3>
            <p>
              {selectedIndustry === 'all' 
                ? 'Layout structure options for all themes'
                : `Layout variations for ${selectedIndustry.replace('_', ' ')} themes`
              }
            </p>
            
            {loading ? (
              <div className="loading">Loading layouts...</div>
            ) : (
              <div className="layout-grid">
                {getThemesByIndustry()[selectedIndustry].map((theme) => (
                  <div key={theme.code} className={`theme-layouts ${theme.enabled ? 'enabled' : 'disabled'}`}>
                    <div className="theme-header">
                      <h4>{theme.name}</h4>
                      <p>{theme.description}</p>
                      <span className={`theme-status ${theme.enabled ? 'enabled' : 'disabled'}`}>
                        {theme.enabled ? <FaToggleOn /> : <FaToggleOff />}
                        {theme.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    
                    <div className="layouts-container">
                      {Object.entries(theme.config?.layoutOptions || {}).map(([layoutKey]) => 
                        renderLayoutCard(theme, layoutKey)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .design-manager {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }
        
        .design-manager-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .design-manager-header h2 {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-text, #2C3E50);
          margin: 0 0 10px 0;
        }
        
        .design-manager-header p {
          color: var(--color-text-secondary, #7F8C8D);
          margin: 0;
        }
        
        .design-manager-content {
          display: grid;
          grid-template-columns: 1fr;
          gap: 30px;
        }
        
        .tab-navigation {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          padding: 15px;
          background: var(--color-surface, #FFFFFF);
          border-radius: 8px;
          border: 1px solid var(--color-border, #E2E8F0);
        }
        
        .tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: 2px solid var(--color-border, #E2E8F0);
          border-radius: 8px;
          background: var(--color-background, #FFFFFF);
          color: var(--color-text, #2C3E50);
          cursor: pointer;
          transition: var(--transition-fast, 0.15s ease);
          font-size: 14px;
        }
        
        .tab-btn:hover {
          border-color: var(--color-primary, #D4AF37);
          background: var(--color-surface, #F8F9FA);
          transform: translateY(-1px);
        }
        
        .tab-btn.active {
          background: var(--color-primary, #D4AF37);
          color: white;
          border-color: var(--color-primary, #D4AF37);
        }
        
        .industry-filter {
          margin-bottom: 30px;
        }
        
        .industry-filter h3 {
          color: var(--color-text, #2C3E50);
          margin: 0 0 15px 0;
        }
        
        .industry-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 10px;
        }
        
        .industry-btn {
          padding: 12px 16px;
          border: 2px solid var(--color-border, #E2E8F0);
          border-radius: 8px;
          background: var(--color-background, #FFFFFF);
          color: var(--color-text, #2C3E50);
          cursor: pointer;
          transition: var(--transition-fast, 0.15s ease);
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .industry-btn:hover {
          border-color: var(--color-primary, #D4AF37);
          background: var(--color-surface, #F8F9FA);
          transform: translateY(-1px);
        }
        
        .industry-btn.active {
          background: var(--color-primary, #D4AF37);
          color: white;
          border-color: var(--color-primary, #D4AF37);
        }
        
        .design-section, .layout-section {
          background: var(--color-surface, #FFFFFF);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid var(--color-border, #E2E8F0);
        }
        
        .design-section h3, .layout-section h3 {
          color: var(--color-text, #2C3E50);
          margin: 0 0 15px 0;
        }
        
        .design-section p, .layout-section p {
          color: var(--color-text-secondary, #7F8C8D);
          margin: 0 0 10px 0;
        }
        
        .design-grid, .layout-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
        }
        
        .theme-designs, .theme-layouts {
          border: 1px solid var(--color-border, #E2E8F0);
          border-radius: 8px;
          padding: 20px;
          background: var(--color-background, #FFFFFF);
        }
        
        .theme-designs.disabled, .theme-layouts.disabled {
          opacity: 0.6;
        }
        
        .theme-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid var(--color-border, #E2E8F0);
        }
        
        .theme-header h4 {
          color: var(--color-text, #2C3E50);
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }
        
        .theme-header p {
          color: var(--color-text-secondary, #7F8C8D);
          margin: 0;
          font-size: 14px;
        }
        
        .theme-status {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .theme-status.enabled {
          color: var(--color-primary, #D4AF37);
        }
        
        .theme-status.disabled {
          color: var(--color-text-secondary, #7F8C8D);
        }
        
        .designs-container, .layouts-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 16px;
        }
        
        .design-card, .layout-card {
          border: 2px solid var(--color-border, #E2E8F0);
          border-radius: 8px;
          padding: 16px;
          background: var(--color-surface, #F8F9FA);
          transition: all 0.3s ease;
        }
        
        .design-card:hover, .layout-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        .design-card.selected, .layout-card.selected {
          border-color: var(--color-primary, #D4AF37);
          background: var(--color-primary, #D4AF37);
          color: white;
        }
        
        .design-preview, .layout-preview {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
        }
        
        .design-colors {
          display: flex;
          gap: 8px;
        }
        
        .color-swatch {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 2px solid rgba(255,255,255,0.2);
        }
        
        .design-info, .layout-info {
          flex: 1;
        }
        
        .design-header, .layout-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        
        .design-header h5, .layout-header h5 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }
        
        .selected-badge {
          background: rgba(255,255,255,0.2);
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
        }
        
        .design-info p, .layout-info p {
          margin: 0 0 8px 0;
          font-size: 12px;
          line-height: 1.4;
          opacity: 0.9;
        }
        
        .design-features, .layout-features {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        
        .feature-tag {
          background: rgba(255,255,255,0.1);
          color: var(--color-text, #2C3E50);
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 500;
        }
        
        .design-card.selected .feature-tag, .layout-card.selected .feature-tag {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        
        .design-actions, .layout-actions {
          margin-top: 12px;
        }
        
        .layout-visual {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .layout-icon-large {
          font-size: 24px;
          opacity: 0.8;
        }
        
        .layout-structure {
          flex: 1;
        }
        
        .structure-preview {
          border: 1px solid var(--color-border, #E2E8F0);
          border-radius: 4px;
          padding: 8px;
          background: var(--color-surface, #F8F9FA);
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .grid-preview {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 4px;
          width: 80px;
        }
        
        .preview-card {
          background: var(--color-primary, #D4AF37);
          border-radius: 2px;
          height: 12px;
        }
        
        .sidebar-preview {
          display: flex;
          gap: 4px;
          width: 80px;
        }
        
        .preview-sidebar {
          background: var(--color-text-secondary, #7F8C8D);
          width: 20px;
          height: 40px;
          border-radius: 2px;
        }
        
        .preview-content {
          background: var(--color-surface, #F8F9FA);
          flex: 1;
          height: 40px;
          border-radius: 2px;
          border: 1px solid var(--color-border, #E2E8F0);
        }
        
        .hero-preview {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 80px;
        }
        
        .preview-hero {
          background: var(--color-primary, #D4AF37);
          height: 20px;
          border-radius: 2px;
        }
        
        .preview-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 2px;
        }
        
        .magazine-preview {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 80px;
        }
        
        .preview-banner {
          background: var(--color-text-secondary, #7F8C8D);
          height: 16px;
          border-radius: 2px;
        }
        
        .preview-magazine {
          display: flex;
          gap: 2px;
        }
        
        .preview-article {
          background: var(--color-surface, #F8F9FA);
          height: 20px;
          border-radius: 2px;
          flex: 1;
        }
        
        .minimal-preview {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2px;
          width: 80px;
        }
        
        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: var(--transition-fast, 0.15s ease);
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .btn-sm {
          padding: 6px 12px;
          font-size: 11px;
        }
        
        .btn-success {
          background: #10B981;
          color: white;
        }
        
        .btn-warning {
          background: #F59E0B;
          color: white;
        }
        
        .btn:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        
        .loading {
          text-align: center;
          padding: 40px;
          color: var(--color-text-secondary, #7F8C8D);
          font-size: 16px;
        }
      `}</style>
    </div>
  );
};

export default DesignManager;
